/**
 * Doc-upload outcome vocabulary — Lane 616 (Phase 5, the front door).
 *
 * WHY THIS IS A SEPARATE, PURE MODULE
 * -----------------------------------
 * `POST /api/chat/upload/evaluate` answers with a `status` string, and the
 * WHOLE point of Phase 1 (Lane 581) was that a document we could not read must
 * fail HONESTLY instead of looking processed. That guarantee is only as good as
 * the surface that renders it: if the UI paints a success tick for a status
 * that means "unreadable", Phase 1's guard is defeated at the last inch.
 *
 * So the status -> outcome mapping lives here, pure and DOM-free, where it can
 * be unit-tested against the endpoint's real vocabulary rather than inferred
 * from a rendered blob.
 *
 * THE INVARIANT (test-pinned, both directions)
 * --------------------------------------------
 *   `READ_STATUSES` is an EXPLICIT allow-list of the only statuses that
 *   mean "Wingman read this document and recorded what it found" (the two
 *   claim outcomes, plus Lane 1400's `table_ready`: a spreadsheet stored as
 *   rows).
 *   Everything else — including a status this build has never heard of —
 *   resolves to a NON-read outcome.
 *
 * That direction matters. A future pipeline status defaulting to "read" would
 * be a fabrication about a customer's document; defaulting to "we don't know"
 * is merely unhelpful. See `OUTCOME.UNKNOWN`.
 *
 * SERVER COPY IS RENDERED VERBATIM, NEVER RE-WRITTEN
 * --------------------------------------------------
 * Every failure the pipeline can produce already carries a human-readable
 * `rationale` composed by the engine — the OCR refusal kinds (`too_many_pages`,
 * `too_large`, `truncated`, `over_budget`, `refusal`, `empty`, `vision`,
 * `unreadable`) each surface a specific `_detail` string, and the empty-document
 * guard wraps them as "Could not read any text from this document: <detail>".
 * This module SURFACES that string; it does not paraphrase it. Paraphrasing a
 * refusal reason is how a UI ends up telling a buyer something the engine never
 * said (Rule 116 family).
 *
 * MIRRORED SERVER CONSTANTS
 * -------------------------
 * `MAX_UPLOAD_BYTES` and `ALLOWED_MIME_TYPES` are mirrors of
 * `backend/helpers/doc_upload.py`. They exist ONLY so the buyer gets an instant,
 * honest answer instead of pushing 25 MB up the wire to earn a 413/415. The
 * SERVER remains the authority: both server rejections are rendered too, and a
 * mirror that drifts merely costs a wasted round trip — it can never let through
 * something the server refuses.
 */

// ── mirrors of backend/helpers/doc_upload.py ────────────────────────────────
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

export const ALLOWED_MIME_TYPES = Object.freeze([
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'text/plain',
]);

/**
 * Lane 1400 WS1 — why a file never reached Wingman, as a short code. The
 * chat sends only the code (never our sentence) in `failed_uploads`; the
 * server owns the words. Every pre-storage failure object below carries it
 * as `failureCode`, with the file's name as `fileName`, so the chat can say
 * which file did not make it.
 */
export const FAILURE_CODE = Object.freeze({
  TOO_LARGE: 'too_large',
  UNSUPPORTED_TYPE: 'unsupported_type',
  NETWORK: 'network',
  UPLOAD_FAILED: 'upload_failed',
});

/** `accept` attribute for the file input — extensions + the mirrored MIMEs. */
export const ACCEPT_ATTR = '.pdf,.docx,.xlsx,.txt,' + ALLOWED_MIME_TYPES.join(',');

export const SUPPORTED_HUMAN = 'PDF, Word (.docx), Excel (.xlsx) and plain text';

/**
 * Lane 1400 WS3 (CHAT_UPLOAD_TABLES_ENABLED, echoed by /me as
 * `upload_tables_enabled`): a .csv is admitted too and stored as a table.
 * Excel on Windows labels a .csv `application/vnd.ms-excel`; that label is
 * accepted only for a `.csv` file name, mirroring the server's
 * `upload_tables.normalize_upload_mime`. Flag OFF uses none of this.
 */
export const CSV_MIME_TYPE = 'text/csv';
const EXCEL_CSV_ALIAS_MIME = 'application/vnd.ms-excel';
export const SUPPORTED_HUMAN_WITH_CSV = 'PDF, Word (.docx), Excel (.xlsx), CSV and plain text';

/** The file input's `accept` list for the current flag state. */
export function acceptAttrFor(csvEnabled) {
  return csvEnabled ? `${ACCEPT_ATTR},.csv,${CSV_MIME_TYPE}` : ACCEPT_ATTR;
}

function isCsvFile(file) {
  const type = file.type || '';
  if (type === CSV_MIME_TYPE) return true;
  return type === EXCEL_CSV_ALIAS_MIME && /\.csv$/i.test(file.name || '');
}

/** The status for a spreadsheet stored as typed rows (Lane 1400 WS3). */
export const TABLE_READY_STATUS = 'table_ready';

/**
 * Outcome classes. The UI keys tone and affordances off these, never off the
 * raw status string — so a new status cannot silently inherit success styling.
 */
export const OUTCOME = Object.freeze({
  READING: 'reading', // in flight, or the server says it is still working
  READ: 'read', // read AND recorded — the only success class
  NOTHING_FOUND: 'nothing_found', // read fine, recognised nothing. NOT success.
  AWAITING_SUPPLIER: 'awaiting_supplier', // read, NOTHING recorded, needs a pick
  DUPLICATE: 'duplicate', // we already have this exact file
  BLOCKED: 'blocked', // refused before reading (disabled / too big / wrong type)
  FAILED: 'failed', // tried and could not read or store it
  UNKNOWN: 'unknown', // a status this build does not recognise — fail closed
});

/**
 * THE ALLOW-LIST. The two terminal statuses from
 * `helpers/doc_upload_pass4._derive_final_status` that mean claims landed,
 * plus `table_ready` (Lane 1400 WS3): a spreadsheet whose rows were stored as
 * a table the chat can query. Mirrors `doc_upload.CHAT_READABLE_UPLOAD_STATUSES`.
 *
 * Deliberately NOT included:
 *   `no_assertions`  — the text was read but nothing was recognised. True, and
 *                      worth saying, but it is not "we captured this letter".
 *                      Under a guard-OFF tenant it is ALSO the status an empty
 *                      document lands on via the legacy silent-success path,
 *                      which is precisely the outcome Phase 1 exists to stop
 *                      reading as a success.
 *   `extracting`     — an intermediate row status (see STATUS_NOTES below).
 *   `awaiting_supplier` — Lane 618's notice park. The text WAS read and its
 *                      claims verified, but the pipeline wrote **ZERO**
 *                      assertions: nothing is attributed until the buyer picks
 *                      a supplier (`POST .../attach-supplier`). Rendering it as
 *                      READ would claim a capture that has not happened.
 */
export const READ_STATUSES = Object.freeze([
  'fully_processed',
  'partial_success',
  // Lane 1400 WS3: a spreadsheet stored as typed rows the chat can query.
  // Read and stored, so it IS the success class; only the server's
  // CHAT_UPLOAD_TABLES_ENABLED path can return it.
  TABLE_READY_STATUS,
]);

/**
 * Statuses that mean "this document did NOT yield usable content". Kept as an
 * explicit list so the test can assert it never intersects READ_STATUSES —
 * an executable form of "never show success for a document we could not read".
 */
export const UNREADABLE_STATUSES = Object.freeze([
  'extract_error',
  'upload_error',
  'error',
]);

/**
 * Every status the endpoint can return today, for the exhaustiveness test.
 * Sources: `evaluate_uploaded_document` return sites + Pass 4's four-way switch
 * + the route's own documented mapping.
 *
 * NOTE on `extracting`: `backend/routes/doc_upload.py`'s docstring documents it
 * as a returned status, but that table dates from Commit ε when Pass 1+ was
 * dormant. Today the pipeline runs synchronously through Pass 4 and returns a
 * terminal status, so no live return site emits it. It is handled here anyway —
 * as "still reading", never as success — because the route contract still
 * names it and a future async split would bring it straight back.
 */
export const KNOWN_STATUSES = Object.freeze([
  'fully_processed',
  'partial_success',
  'no_assertions',
  'awaiting_supplier',
  'extract_error',
  'upload_error',
  'duplicate',
  'disabled',
  'extracting',
  'error',
  TABLE_READY_STATUS,
]);

/**
 * The notice park (Lane 618). `helpers/doc_upload.py` classifies a supplier
 * commercial notice, persists its verified claims, writes **zero** assertions
 * and parks the row here — because attribution is the buyer's decision and the
 * picker is a round trip through them (execution plan §15.6).
 *
 * Kept as its own named constant because the number that matters on this
 * branch is `notice.claims_available`: it is what the buyer is about to
 * attribute, and it is NOT a count of anything recorded.
 */
export const AWAITING_SUPPLIER_STATUS = 'awaiting_supplier';

/**
 * The status `helpers/candidate_lifecycle.py:105` writes when a buyer RETRACTS
 * a document's claims (`retracted_status: 'rolled_back'`). It is a row status,
 * never an envelope status — it can only ever reach this module as the
 * `prior_status` of a duplicate — which is why it is not in `KNOWN_STATUSES`.
 */
export const ROLLED_BACK_STATUS = 'rolled_back';

/**
 * Mirror of `helpers/doc_upload.py DEDUP_ELIGIBLE_STATUSES` — every value that
 * can arrive as a duplicate's `prior_status`, because `_check_dedup` only
 * short-circuits on rows in that set.
 *
 * Deliberately NOT `KNOWN_STATUSES`. That list is the ENVELOPE's own status
 * vocabulary; this one is the PRIOR ROW's, and the two genuinely differ:
 * `rolled_back` and `unresolved_category` appear only here, and `duplicate` /
 * `disabled` / `extract_error` / `upload_error` appear only there. Checking a
 * prior status against the envelope list would misclassify a retraction as
 * unrecognised — which, under `duplicateAction` below, is the difference
 * between "leave it alone" and "read it again".
 */
export const PRIOR_STATUSES = Object.freeze([
  'extracting',
  'fully_processed',
  'partial_success',
  'no_assertions',
  'unresolved_category',
  ROLLED_BACK_STATUS,
  AWAITING_SUPPLIER_STATUS,
  TABLE_READY_STATUS,
]);

/**
 * What a duplicate card should DO — D50.5, Ryan's decision, verbatim:
 * *"You can say read it again if it needs to, or maybe it doesn't need to read
 * it again. As long as it pulls the information."*
 *
 * The second half of that sentence is the binding condition and it is what
 * chose between the two halves of the first.
 */
export const DUPLICATE_ACTION = Object.freeze({
  /** The prior row is PARKED. The picker already on screen IS the answer. */
  CONFIRM: 'confirm',
  /** Read it again — automatically, once, with no click. */
  REREAD: 'reread',
  /** Never automatic. Say what happened and let the buyer decide. */
  ASK: 'ask',
});

/**
 * ⚠️ WHY "REUSE THE PRIOR PARSE" IS NOT ONE OF THE OPTIONS ABOVE.
 * ---------------------------------------------------------------
 * It was the proposed design for this lane, and it is WRONG in a way that is
 * invisible from the client. consult-architect caught it 2026-08-02 and Ryan
 * resolved the escalation by choosing the re-read (Option 2).
 *
 * The letter's claims only reach the answer when the CONVERSATION knows which
 * supplier it is about. `get_notice_claims_context_for_ai` returns `""` the
 * moment `scope_supplier_names` is falsy (`helpers/notice_claims.py:501-503`),
 * and that list is derived exclusively from the QUERY's own scope resolution
 * (`routes/ai_chat.py:1121-1148`). The one and only thing that makes a freshly
 * uploaded letter's supplier the conversation's subject is the buyer's CONFIRM:
 * `establish_notice_subject` is called from exactly one site in the codebase,
 * the attach endpoint (`routes/doc_upload.py:440`).
 *
 * So a branch that skipped the confirm and "reused" the prior parse would
 * deliver the letter ONLY on turns where the buyer happened to name the
 * supplier out loud, and would silently deliver NOTHING on every other turn —
 * strictly worse than the click Ryan complained about. Re-attaching the prior
 * candidate cannot rescue it either: `attach_supplier` raises 409 while the
 * prior claims are active (`helpers/notice_write.py:312-317`).
 *
 * A future lane MAY add reuse — but only once the duplicate response carries
 * the prior row's resolved supplier (register item **F-3**) and that supplier
 * is used to set the conversation anchor. Until then, the re-read is the only
 * path that provably satisfies "as long as it pulls the information", because
 * the re-read parks the letter and the sender confirm — which D50.1 keeps —
 * runs and anchors exactly as it does on a first upload.
 */
export function duplicateAction(state) {
  if (!state || state.outcome !== OUTCOME.DUPLICATE) return null;

  // The Lane 676 second door. The prior row is parked at `awaiting_supplier`
  // with zero assertions written and is still attachable, so the picker the
  // card already renders writes the claims and sets the anchor — everything a
  // re-read would achieve, without a second candidate row or a second paid
  // extraction. Re-reading here would ALSO fork the claim timeline: the six
  // notice predicates are `allow_multiple=True`, so nothing supersedes.
  if (state.priorAwaitingSupplier === true) return DUPLICATE_ACTION.CONFIRM;

  const prior = state.priorStatus;

  // FAIL CLOSED on anything we cannot classify — including a backend older than
  // Lane 667, which omits `prior_status` entirely. `Home.duplicateNotice.test.js`
  // pins that case, and the pin's whole point is that an unrecognised prior must
  // not be acted on. Never spend a paid extraction on a guess.
  if (typeof prior !== 'string' || !PRIOR_STATUSES.includes(prior)) {
    return DUPLICATE_ACTION.ASK;
  }

  // A DELIBERATE RETRACTION IS NEVER RESURRECTED AUTOMATICALLY. `rolled_back`
  // means the buyer removed these claims on purpose (Rule 107 soft-delete). For
  // a notice a re-read would at least stop at the sender confirm, but an
  // ORDINARY document's assertions are written straight through by Pass 4 with
  // no confirm gate — so an automatic re-read would silently restore data the
  // buyer had just taken out. The button stays; the automation does not.
  if (prior === ROLLED_BACK_STATUS) return DUPLICATE_ACTION.ASK;

  return DUPLICATE_ACTION.REREAD;
}

/**
 * The card WHILE the automatic re-read is in flight.
 *
 * ⚠️ THE COPY RULE, which an earlier draft of this lane broke. Say only what
 * the client can OBSERVE. It can observe that this file is already on file and
 * that a fresh read has been fired — it cannot observe what any answer went on
 * to use, so it must never say "is being used in this answer" (the same
 * honesty rule `chatDocumentWait.js`'s copy note states for the honest-note).
 *
 * `outcome: READING` is load-bearing beyond tone: it is what keeps the chat
 * turn waiting (`chatDocumentWait.slotWaitState`). See the seam in `Home.js`
 * `runUpload` for why this state must be written INSTEAD OF the duplicate
 * state and never after it.
 */
export function describeRereading(label, priorProcessedAt) {
  const day = priorProcessedAt ? String(priorProcessedAt).slice(0, 10) : '';
  return {
    outcome: OUTCOME.READING,
    status: 'duplicate',
    rereading: true,
    headline: `Wingman already has ${label}. Reading it again…`,
    detail: day
      ? `This exact file was read on ${day}. Wingman is reading it again so you `
        + 'can confirm who sent it.'
      : 'This exact file has already been read. Wingman is reading it again so '
        + 'you can confirm who sent it.',
    canRetrySameFile: false,
  };
}

function plural(n, one, many) {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * The `awaiting_supplier` detail line.
 *
 * The ZERO case is called out separately and deliberately. A notice can
 * classify as a notice and still carry no verified claims — every field can be
 * dropped by `notice_extraction.py`'s evidence check. Saying "with 0 …" beside
 * a picker would invite the buyer to confirm a supplier in the belief
 * something gets recorded, when the attach would write nothing. So the zero
 * case says what confirming will actually do.
 *
 * The rendered noun is NOT "claims" (D31(a)): the store's word reads as
 * *disputed* to a buyer, when what is being counted is what the letter SAID.
 * `claims_available` stays the field name; the sentence names the provenance.
 * This branch is the notice flow and shares its vocabulary with
 * `noticeAttachStatus.js`, so the card the buyer confirms on and the card that
 * reports the result use one word for one thing.
 */
function awaitingDetail(claimsAvailable) {
  if (claimsAvailable === null) {
    return 'It reads as a supplier notice. Nothing is recorded until you confirm who sent it.';
  }
  if (claimsAvailable === 0) {
    return (
      'It reads as a supplier notice, but Wingman found nothing in it that it could ' +
      'verify against the text. Confirming the sender will record who it was, and nothing else.'
    );
  }
  return (
    `It reads as a supplier notice telling us ${plural(claimsAvailable, 'thing', 'things')}. ` +
    'Nothing is recorded until you confirm who sent it.'
  );
}

/**
 * A5 — the unknown-entity sentence (Lane 867, reworded by Lane 875 on Ryan's
 * D11).
 *
 * ⚠️ THE WORDING IS THE WHOLE POINT, so read this before changing a word of it.
 *
 * ── WHAT IT MAY NEVER SAY, AND WHY THE VERB IS "MATCH" ────────────────────
 * The build plan proposed *"9 items on this file aren't in your system yet"*.
 * That sentence asserts a fact about the CUSTOMER'S DATA, and it is false. An
 * entity is unresolved for either of two reasons the pipeline genuinely cannot
 * tell apart: it does not exist, or it exists and the matcher missed it. On the
 * real document this was built from, BOTH causes are present — `Textron` and the
 * seven part numbers really are absent, while `RUBBER TECHNOLOGY` exists as
 * `Grt Rubber Technologies L.L.C.` and was missed because "technology" is not a
 * substring of "technologies". Telling a buyer a supplier is not in their system
 * when it is, is exactly the class of confident-and-wrong statement this codebase
 * spends its effort preventing.
 *
 * So the copy says what WINGMAN DID — "couldn't match" — never what is true of
 * the buyer's data. That is honest today, stays honest when the matcher improves,
 * and needs no runtime distinction the pipeline cannot make. **Ryan's D11 is an
 * objection to the PHRASING, explicitly not to the caution**, so that property is
 * unchanged and is pinned by an absence test.
 *
 * ── WHAT D11 CHANGED (Ryan: *"They can say everything got recorded and what
 * didn't."*) ───────────────────────────────────────────────────────────────
 * The old card gave a buyer a numerator with no denominator. It read:
 *
 *     Recorded 152 claims from this document. Wingman couldn’t match 10 things
 *     on this file to anything in your data — materials …; the customer …
 *
 * Three things made that hard to read, and all three are fixed here:
 *   1. TWO NOUNS FOR ONE FILE — "this document" then "this file", one sentence
 *      apart, reading as if two different things were being described. When this
 *      sentence follows a recorded count, that count now names no file at all
 *      (the card's HEADLINE already names it: `Read HOT LIST SKF 7.10.26.xlsx.`),
 *      so exactly one noun survives and the two numbers land side by side:
 *      **"Recorded 152 claims. Wingman couldn’t match 10 things on this file…"**
 *      With NO companion sentence the recorded line keeps its old wording — see
 *      `describeOutcome`'s READ branch for why that is load-bearing, not
 *      inconsistency.
 *   2. "to anything in your data —" was a mouthful ending in an em dash that
 *      had to do a colon's job. Now "to your data:", which introduces the list.
 *   3. THE ZERO CASE USED A DIFFERENT VERB from the has-gaps case ("was already
 *      in your data" vs "couldn't match"), so the two outcomes did not read as
 *      answers to the same question. Both now turn on "match" — Ryan's own "one
 *      word everywhere" call from Lane 861.
 *
 * ⚠️ THE PAIRING IS BY ADJACENCY, NOT BY A PARAMETER, and that is deliberate.
 * This function stays context-free: it is appended to the READ branch (where a
 * count precedes it), to `partial_success` (where "Recorded 5 of 12 claims"
 * precedes it) and to `no_assertions` (where NOTHING was recorded and the
 * headline says so). A word like "10 OTHER things" would be false on that third
 * branch, so the sentence must not presume a first half exists.
 *
 * THE ZERO CASE IS SPOKEN, NOT SKIPPED. When the flag is on and everything
 * matched, the card says so. Absence rendered as a positive fact is the point of
 * the feature: silence is the vacuum a reader fills with an assumption. The key
 * being ABSENT (flag off / older backend) is a different thing from the key being
 * an EMPTY ARRAY (we checked, nothing was missed) and the two must not collapse.
 */
export const UNKNOWN_ENTITY_RENDER_CAP = 12;

/** D11: all three plurals now carry "the", so the groups read in parallel —
 *  "the materials A and B; the customer C; and the suppliers D and E". The
 *  material plural was the odd one out ("materials A and B") and the asymmetry
 *  showed in the middle of the longest sentence on the card. */
const UNKNOWN_TYPE_NOUNS = Object.freeze({
  material: ['the material', 'the materials'],
  account: ['the customer', 'the customers'],
  supplier: ['the supplier', 'the suppliers'],
});

/** `a, b and c` — the buyer reads this, so it is a list not a join. */
function humanList(names) {
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * Build the sentence from the server's `unknown_entities` array.
 * Returns '' when the input is not a usable array, so callers can append
 * unconditionally.
 */
export function unknownEntitiesSentence(unknownEntities) {
  if (!Array.isArray(unknownEntities)) return '';
  const items = unknownEntities.filter(
    (e) => e && typeof e.name === 'string' && e.name.trim(),
  );
  if (items.length === 0) {
    return 'Wingman matched everything it recognised on this file to your data.';
  }

  const shown = items.slice(0, UNKNOWN_ENTITY_RENDER_CAP);
  const remainder = items.length - shown.length;

  // Group in the order the server sorted them, so the sentence reads
  // materials -> customers -> suppliers without re-sorting here.
  const segments = [];
  let run = null;
  shown.forEach((item) => {
    const type = item.entity_type;
    if (!run || run.type !== type) {
      run = { type, names: [] };
      segments.push(run);
    }
    run.names.push(item.name.trim());
  });

  const rendered = segments.map((seg) => {
    const nouns = UNKNOWN_TYPE_NOUNS[seg.type];
    const list = humanList(seg.names);
    // An unrecognised entity_type still gets its names said out loud — a
    // future type must never silently drop names off the card.
    if (!nouns) return list;
    return `${seg.names.length === 1 ? nouns[0] : nouns[1]} ${list}`;
  });

  let tail;
  if (rendered.length === 1) {
    [tail] = rendered;
  } else {
    tail = `${rendered.slice(0, -1).join('; ')}; and ${rendered[rendered.length - 1]}`;
  }
  if (remainder > 0) {
    tail += `; and ${remainder} more`;
  }

  return (
    `Wingman couldn’t match ${plural(items.length, 'thing', 'things')} on this ` +
    `file to your data: ${tail}.`
  );
}

/**
 * Lane 1400 WS3: the `table_ready` detail line, from the server's `tables`
 * summary. Plain English, counts only; the truncation sentence appears only
 * when the server says rows were left out.
 */
export function tablesSentence(tables) {
  const rows = Number(tables && tables.rows);
  const sheets = Number(tables && tables.sheets);
  if (!Number.isFinite(rows) || !Number.isFinite(sheets) || rows < 0 || sheets < 1) {
    return 'Stored it as a table you can ask about in chat.';
  }
  const coverage = tables.coverage;
  const total = Number(coverage && coverage.rows_total);
  if (tables.truncated === true && coverage && coverage.rows_total_exact === true
      && Number.isFinite(total) && total > rows) {
    // Lane 1413: the server counted the whole file, so say how much is stored.
    const percent = Math.max(1, Math.min(99, Math.round((100 * rows) / total)));
    return `Stored ${rows.toLocaleString('en-US')} of ${total.toLocaleString('en-US')} rows `
      + `(${percent}%) as a table; the rest of the file was over the table limit.`;
  }
  let text = `Read ${rows.toLocaleString('en-US')} ${rows === 1 ? 'row' : 'rows'} from `
    + `${sheets.toLocaleString('en-US')} ${sheets === 1 ? 'sheet' : 'sheets'}.`;
  if (tables.truncated === true) {
    text += ' The file was larger than the reading limit, so some rows were not read.';
  }
  return text;
}

/** Human file size, honest at both ends (`0.4 MB`, `31.2 MB`). */
export function humanSize(bytes) {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Client-side pre-flight. Returns null when the file should be sent.
 *
 * Mirrors the two rejections the route raises before any storage work
 * (415 Content-Type, 413 size). A browser sends `File.type` as the multipart
 * part's Content-Type and substitutes `application/octet-stream` when it is
 * empty, so checking `file.type` is a faithful predictor of the server's
 * verdict rather than a second, stricter opinion.
 */
export function preflight(file, { csvEnabled = false } = {}) {
  if (!file) return null;
  const contentType = file.type || 'application/octet-stream';
  if (!ALLOWED_MIME_TYPES.includes(contentType) && !(csvEnabled && isCsvFile(file))) {
    return {
      outcome: OUTCOME.BLOCKED,
      headline: `Wingman can’t read this kind of file.`,
      detail: `${file.name} is ${contentType}. Supported: ${
        csvEnabled ? SUPPORTED_HUMAN_WITH_CSV : SUPPORTED_HUMAN}.`,
      canRetrySameFile: false,
      fileName: file.name,
      failureCode: FAILURE_CODE.UNSUPPORTED_TYPE,
    };
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    return {
      outcome: OUTCOME.BLOCKED,
      headline: 'That file is too large.',
      detail: `${file.name} is ${humanSize(file.size)}. The limit is ${humanSize(MAX_UPLOAD_BYTES)}.`,
      canRetrySameFile: false,
      fileName: file.name,
      failureCode: FAILURE_CODE.TOO_LARGE,
    };
  }
  return null;
}

/**
 * Map an endpoint response body to a rendered outcome.
 *
 * @param {object} body   parsed JSON from POST /api/chat/upload/evaluate
 * @param {string} name   the file name, for the headline
 */
export function describeOutcome(body, name) {
  const label = name || 'this document';
  const status = body && typeof body.status === 'string' ? body.status : null;
  // Server-authored text, surfaced verbatim. Never re-worded here.
  const rationale = body && typeof body.rationale === 'string' ? body.rationale : '';
  const candidateId = body && body.candidate_id != null ? body.candidate_id : null;
  // A5. Absent unless the server ran the check (see `unknownEntitiesSentence`).
  // `undefined` and `[]` mean different things and are kept apart on purpose.
  const unknownSentence = body ? unknownEntitiesSentence(body.unknown_entities) : '';
  const withUnknowns = (detail) =>
    (unknownSentence ? `${detail} ${unknownSentence}` : detail);

  if (status === TABLE_READY_STATUS) {
    return {
      outcome: OUTCOME.READ,
      status,
      candidateId,
      headline: `Read ${label}.`,
      detail: tablesSentence(body && body.tables),
      canRetrySameFile: false,
    };
  }

  if (READ_STATUSES.includes(status)) {
    const pass4 = (body && body.pass_4) || {};
    const written = Number(pass4.assertions_written);
    const planned = Number(pass4.planned_claims_total);
    let detail;
    if (status === 'partial_success' && Number.isFinite(written) && Number.isFinite(planned)) {
      // Say the shortfall out loud — a partial success that renders like a full
      // one is the same lie in a smaller font.
      detail = `Recorded ${written} of ${planned} claims. The rest could not be recorded.`;
    } else if (Number.isFinite(written)) {
      // ⚠️ D11 — TWO WORDINGS, AND THE CONDITION IS "DOES A COMPANION SENTENCE
      // FOLLOW", not a flag. When `unknownSentence` is present it says "on this
      // file", so keeping "from this document" here put TWO different nouns on
      // ONE document a sentence apart — the phrasing Ryan objected to. The
      // headline directly above already names the file (`Read <name>.`), so the
      // shorter form loses nothing and the two counts land side by side.
      //
      // When nothing follows, this line is the card's ENTIRE detail and keeps
      // its pre-D11 wording — so every tenant without `UPLOAD_INTEL_ENABLED`
      // (which is every tenant on PROD today) sees a byte-identical card. That
      // is `flag OFF is byte-identical` in docUploadStatus.test.js, and it is an
      // acceptance criterion inherited from Lane 867, not a nicety.
      const count = plural(written, 'claim', 'claims');
      detail = unknownSentence ? `Recorded ${count}.` : `Recorded ${count} from this document.`;
    } else {
      detail = 'Recorded what it found.';
    }
    return {
      outcome: OUTCOME.READ,
      status,
      candidateId,
      headline: `Read ${label}.`,
      detail: withUnknowns(detail),
      canRetrySameFile: false,
    };
  }

  if (status === 'no_assertions') {
    return {
      outcome: OUTCOME.NOTHING_FOUND,
      status,
      candidateId,
      headline: `Read ${label}, but found nothing to record.`,
      // The unknown list matters MOST here. "Found nothing to record" plus the
      // names of what it could not match is an explanation; without them it is
      // a dead end the buyer cannot act on. Note this branch is reached from
      // two places — Pass 1 finding zero entities (no list, key absent) and
      // Pass 4's terminal `no_assertions` (list present) — so the sentence
      // appears only when the pipeline actually got far enough to check.
      detail: withUnknowns(
        rationale ||
          'Wingman read the text and did not recognise any supplier or material in it.',
      ),
      canRetrySameFile: true,
    };
  }

  if (status === AWAITING_SUPPLIER_STATUS) {
    // Read, verified, and attributed to NOBODY. The pipeline wrote zero
    // assertions on purpose: this is beat one of two, and beat two is the
    // buyer's pick. So this is neither a success (nothing is recorded) nor a
    // failure (nothing went wrong) — it is a question, and it renders as one.
    const notice = (body && body.notice) || {};
    const available = Number(notice.claims_available);
    const claimsAvailable = Number.isFinite(available) && available >= 0 ? available : null;
    // `complete_read: false` does NOT mean the read failed — it means we cannot
    // assert what is ABSENT from the document, so `notice_extraction.py`
    // degrades those fields to `unknown`. The claims that survived are still
    // quote-verified. Carried for the card to note, never rendered as an error.
    const completeRead = notice.complete_read === true;
    return {
      outcome: OUTCOME.AWAITING_SUPPLIER,
      status,
      candidateId,
      claimsAvailable,
      completeRead,
      headline: `Read ${label}. Which supplier sent it?`,
      detail: awaitingDetail(claimsAvailable),
      canRetrySameFile: false,
    };
  }

  if (status === 'duplicate') {
    const when = body && body.prior_processed_at ? String(body.prior_processed_at) : '';
    const day = when ? when.slice(0, 10) : '';
    // ══ SEAM — A5a (Lane 676) ══
    // `prior_status` is the PRIOR row's own status (Lane 667,
    // `helpers/doc_upload.py`). It is NOT this envelope's `status`, which is the
    // literal 'duplicate' every branch above keys on — naming it `status` would
    // have broken this very function, and the engine comments say so.
    //
    // WHAT IT LETS US SAY, AND THE MUCH LARGER SET IT DOES NOT
    // --------------------------------------------------------
    // Exactly ONE value is actionable: `awaiting_supplier` means the prior row
    // is a PARKED notice — read, quote-verified, and attributed to NOBODY, with
    // zero assertions written and the row still attachable. That is the state a
    // re-upload used to dead-end on, and it is the whole reason the field was
    // added: `assertions_created_count` reads 0 for a parked notice AND for a
    // document we read and found nothing in, so a count cannot tell them apart.
    //
    // ⚠️ EVERY OTHER VALUE IS DELIBERATELY TREATED AS "WE DO NOT KNOW", and the
    // reason is measured, not cautious. `helpers/notice_write.attach_supplier`
    // writes `STATUS_CLAIMS_WRITTEN = 'fully_processed'` — the SAME literal
    // Pass 4 writes for an ordinary document. So a notice whose sender is
    // already confirmed and a plain processed PDF are INDISTINGUISHABLE here,
    // and there is no shipped GET on a candidate to break the tie (the module
    // exposes only POST evaluate / retract / attach-supplier). Inferring "a
    // supplier is attributed" from `fully_processed` would therefore be a
    // guess, and a wrong guess routes the buyer into a deck built for a
    // supplier we never established. We do not guess.
    const priorStatus =
      body && typeof body.prior_status === 'string' ? body.prior_status : null;
    const priorAwaitingSupplier = priorStatus === AWAITING_SUPPLIER_STATUS;

    if (priorAwaitingSupplier) {
      return {
        outcome: OUTCOME.DUPLICATE,
        status,
        priorStatus,
        priorAwaitingSupplier: true,
        candidateId,
        // UNKNOWN, not zero. The duplicate body carries no `notice` object, so
        // the claim count is genuinely unavailable — and a parked row reached
        // that state THROUGH a successful extraction, so asserting "nothing
        // else to record" here would be a fabrication about the buyer's own
        // document. `null` is the established unknown sentinel on this branch
        // (see `awaitingDetail`), and `confirmConsequence` renders it as such.
        claimsAvailable: null,
        headline: `Wingman already has ${label}.`,
        detail: day
          ? `This exact file was read on ${day}, and nobody has said who sent it yet. `
            + 'Nothing is recorded until you confirm the sender.'
          : 'This exact file has already been read, and nobody has said who sent it yet. '
            + 'Nothing is recorded until you confirm the sender.',
        // ?retry=true is the endpoint's own documented bypass. Still offered:
        // re-reading a parked letter is a legitimate thing to want, and it is
        // the only way to refresh a stale extraction.
        canRetrySameFile: true,
        retryLabel: 'Read it again anyway',
      };
    }

    return {
      outcome: OUTCOME.DUPLICATE,
      status,
      priorStatus,
      priorAwaitingSupplier: false,
      candidateId,
      // M4C WS5.4 (Lane 1119): the server's explicit statement that the
      // prior row's stored text can feed a chat turn. STRICTLY `=== true`:
      // an older backend omits the key, and a missing key must read as
      // "not available" during a mixed-version deploy — never as true.
      attachmentTextAvailable: !!(body && body.attachment_text_available === true),
      headline: `Wingman already has ${label}.`,
      detail: day
        ? `This exact file was read on ${day}. Nothing new was recorded.`
        : 'This exact file has already been read. Nothing new was recorded.',
      // ?retry=true is the endpoint's own documented bypass.
      canRetrySameFile: true,
      retryLabel: 'Read it again anyway',
    };
  }

  if (status === 'disabled') {
    return {
      outcome: OUTCOME.BLOCKED,
      status,
      candidateId,
      headline: 'Document upload is not enabled for your company.',
      detail: rationale || 'Ask your Wingman contact to turn it on.',
      canRetrySameFile: false,
    };
  }

  if (status === 'extracting') {
    return {
      outcome: OUTCOME.READING,
      status,
      candidateId,
      headline: `Still reading ${label}…`,
      detail: 'Wingman has the file and is working through it.',
      canRetrySameFile: false,
    };
  }

  if (status === 'upload_error') {
    return {
      outcome: OUTCOME.FAILED,
      status,
      candidateId,
      headline: `Could not store ${label}.`,
      detail: rationale || 'The file did not reach storage.',
      canRetrySameFile: true,
    };
  }

  if (status === 'extract_error' || status === 'error') {
    return {
      outcome: OUTCOME.FAILED,
      status,
      candidateId,
      // This is the branch that renders an OCR refusal — page cap, byte cap,
      // truncated transcription, budget cap, model refusal — plus the
      // empty-document guard. Every one arrives as a specific server string.
      headline: `Could not read ${label}.`,
      detail: rationale || 'Wingman could not get usable text out of this file.',
      canRetrySameFile: true,
    };
  }

  // Fail closed. An unrecognised status is NOT a success.
  return {
    outcome: OUTCOME.UNKNOWN,
    status,
    candidateId,
    headline: `Wingman could not confirm it read ${label}.`,
    detail: rationale || 'The upload finished in a state this version does not recognise.',
    canRetrySameFile: true,
  };
}

/**
 * Map a non-2xx HTTP response to an outcome. The server's `detail` is rendered
 * verbatim for the two pre-storage rejections (415 / 413) because it names the
 * exact type and the exact cap.
 */
export function describeHttpError(httpStatus, detail, name) {
  const label = name || 'this document';
  const text = typeof detail === 'string' && detail ? detail : '';
  const fileName = name || null;
  if (httpStatus === 415) {
    return {
      outcome: OUTCOME.BLOCKED,
      headline: `Wingman can’t read this kind of file.`,
      detail: text || `Supported: ${SUPPORTED_HUMAN}.`,
      canRetrySameFile: false,
      fileName,
      failureCode: FAILURE_CODE.UNSUPPORTED_TYPE,
    };
  }
  if (httpStatus === 413) {
    return {
      outcome: OUTCOME.BLOCKED,
      headline: 'That file is too large.',
      detail: text || `The limit is ${humanSize(MAX_UPLOAD_BYTES)}.`,
      canRetrySameFile: false,
      fileName,
      failureCode: FAILURE_CODE.TOO_LARGE,
    };
  }
  if (httpStatus === 403) {
    return {
      outcome: OUTCOME.BLOCKED,
      headline: 'You do not have access to document upload.',
      detail: text,
      canRetrySameFile: false,
      fileName,
      failureCode: FAILURE_CODE.UPLOAD_FAILED,
    };
  }
  return {
    outcome: OUTCOME.FAILED,
    headline: `Could not read ${label}.`,
    detail: text || `The upload failed (HTTP ${httpStatus}).`,
    canRetrySameFile: true,
    fileName,
    failureCode: FAILURE_CODE.UPLOAD_FAILED,
  };
}

/** Network/transport failure — no HTTP status at all. */
export function describeNetworkError(name) {
  return {
    outcome: OUTCOME.FAILED,
    headline: `Could not send ${name || 'this document'}.`,
    // Lane 1408: the usual cause on Windows is a file open in Excel.
    detail: 'The file could not be sent. If it is open in Excel or another program, close it and attach it again.',
    canRetrySameFile: true,
    fileName: name || null,
    failureCode: FAILURE_CODE.NETWORK,
  };
}
